import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Text,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from '@chakra-ui/react';
import { 
  FiSearch, 
  FiEye, 
  FiEdit2,
  FiMoreVertical,
  FiChevronLeft,
  FiChevronRight,
  FiX,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import config from '../../config/config';
import { getNominationStatusLabel, getNominationStatusColor } from '../../utils/evaluationStatusUtils';
import evaluationService from '../../services/api/evaluationService';
import usePersistentState from '../../hooks/usePersistentState';

/**
 * CommitteeEvaluations component
 * Shows all evaluations submitted by committee members
 * Can be used in both committee and admin views
 * @param {Object} props - Component props
 * @param {boolean} props.isAdminView - Whether this component is being viewed in admin panel
 */
const CommitteeEvaluations = ({ isAdminView = false }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { currentUser = {}, isCommittee } = useAuth() || {}; // Destructure user and role helper
  const user = currentUser;
  
  // Get user data directly from localStorage for more reliable branch access
  const getUserDataFromStorage = () => {
    try {
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        return JSON.parse(userDataString);
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }
    return null;
  };
  
  const userDataFromStorage = useMemo(() => getUserDataFromStorage(), []);
  
  // State
  const [evaluations, setEvaluations] = useState([]);
  const [filteredEvaluations, setFilteredEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  // Persist filters in sessionStorage
  const [searchTerm, setSearchTerm] = usePersistentState('committeeEvaluations.search', '');
  const [statusFilter, setStatusFilter] = usePersistentState('committeeEvaluations.status', 'all');
  const [branchFilter, setBranchFilter] = usePersistentState('committeeEvaluations.branch', 'all');
  const [centerFilter, setCenterFilter] = usePersistentState('committeeEvaluations.center', 'all');
  // Persist current page in sessionStorage
  const [currentPage, setCurrentPage] = usePersistentState('committeeEvaluations.page', 1);
  // Persist per-page in localStorage
  const [itemsPerPage, setItemsPerPage] = usePersistentState('committeeEvaluations.perPage', 10, 'local');
  
  // Pagination
  const totalPages = Math.ceil(filteredEvaluations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEvaluations = filteredEvaluations.slice(startIndex, endIndex);

  // Default branch (for committee non-admin views, default to user's branch; otherwise 'all')
  const defaultBranch = useMemo(() => {
    if (isCommittee && !isAdminView) {
      return userDataFromStorage?.branch_code || user?.branch_code || user?.branch || 'all';
    }
    return 'all';
  }, [isCommittee, isAdminView, user, userDataFromStorage]);

  // Determine if any filter is active (used to style/show clear button prominently)
  const isAnyFilterActive = (
    (searchTerm && searchTerm.trim() !== '') ||
    statusFilter !== 'all' ||
    centerFilter !== 'all' ||
    branchFilter !== defaultBranch
  );

  // Clear all filters to defaults
  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCenterFilter('all');
    setBranchFilter(defaultBranch);
    setCurrentPage(1);
  };

  // Wait for auth context to be ready
  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      setAuthReady(true);
    }
  }, [user]);

  // Fetch evaluations data
  useEffect(() => {
    const fetchEvaluations = async () => {
      if (!authReady) return; 
      
      try {
        setLoading(true);
        let evaluationsData = [];

        try {
          // Fetch evaluations for current user; backend enforces role/branch
          const { data } = await evaluationService.getMyEvaluations({ skip: 0, limit: 500 });
          const evaluationsResponse = Array.isArray(data) ? data : [];

          evaluationsData = evaluationsResponse.map((evaluation) => {
            const nomination = evaluation.nomination || {};
            const evaluator = evaluation.evaluator || {};

            // Candidate name from nomination if present
            const candidateName = nomination.name || `${nomination.first_name || ''} ${nomination.last_name || ''}`.trim() || 'غير محدد';

            // Branch and center info (names may be present depending on backend enrich)
            const branch = nomination.branch_id || nomination.branch || '';
            const branchName = nomination.branch_name || getBranchName(branch) || '';

            const center = nomination.center_id || nomination.center || '';
            const centerName = nomination.center_name || getCenterName(center) || '';

            // Position/job title if available on nomination
            // Nomination status for display (use frontend utils for label/color)
            const nominationStatus = nomination.status || '';

            // Overall score and question counts
            const scoreFields = [
              'society_knowledge_score',
              'social_responsibility_score',
              'vision_2030_score',
              'center_plan_score',
              'education_score',
              'experience_score',
              'personal_statement_score',
              'interview_score',
            ];
            const score = typeof evaluation.overall_score === 'number' ? evaluation.overall_score : 0;
            const totalQuestions = scoreFields.filter((field) => evaluation[field] !== null && evaluation[field] !== undefined).length;

            const candidateId = nomination.id || evaluation.nomination_id || '';

            return {
              id: evaluation.id || '',
              candidateId,
              candidateName,
              branch,
              branchName,
              center,
              centerName,
              nominationStatus,
              status: evaluation.status || 'draft',
              score,
              totalQuestions,
              recommendation: evaluation.recommendation || 'not_specified',
              submissionDate: evaluation.updated_at || evaluation.created_at || new Date().toISOString(),
              evaluator: evaluator.full_name || '',
              comments: evaluation.comments || '',
            };
          });
        } catch (apiError) {
          console.error('API error:', apiError);
          toast({
            title: 'خطأ في جلب البيانات',
            description: 'حدث خطأ أثناء جلب بيانات التقييمات.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
        
        console.log('Processed evaluations:', evaluationsData);
        
        setEvaluations(evaluationsData);
        setFilteredEvaluations(evaluationsData);
        
        // Set branch filter based on user's branch if they are committee member
        // Don't restrict branch filter for admin view
        if (isCommittee && !isAdminView) {
          // First try to get branch_code from localStorage (most reliable)
          if (userDataFromStorage?.branch_code) {
            console.log('Setting branch from localStorage branch_code:', userDataFromStorage.branch_code);
            setBranchFilter(userDataFromStorage.branch_code);
          }
          // Then try from currentUser object
          else if (user?.branch_code) {
            console.log('Setting branch from user.branch_code:', user.branch_code);
            setBranchFilter(user.branch_code);
          }
          // Fall back to user.branch if available
          else if (user?.branch) {
            console.log('Setting branch from user.branch:', user.branch);
            setBranchFilter(user.branch);
          }
          // Log if we couldn't find the branch code
          else {
            console.warn('Could not find branch_code for committee user:', { 
              user, 
              userDataFromStorage,
              'localStorage userData': localStorage.getItem('userData')
            });
          }
        }
      } catch (error) {
        console.error('Error in evaluation processing:', error);
        toast({
          title: 'خطأ',
          description: 'حدث خطأ أثناء معالجة بيانات التقييمات.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluations();
  }, [toast, user, authReady, isCommittee, isAdminView, setBranchFilter, userDataFromStorage]);

  // Track initial render for pagination resets
  const didMountRef = useRef(false);

  // Apply filters; skip resetting page on initial mount to preserve persisted page
  useEffect(() => {
    let filtered = [...evaluations];

    if (searchTerm) {
      filtered = filtered.filter(
        (evaluation) =>
          (evaluation.candidateName && evaluation.candidateName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (evaluation.evaluator && evaluation.evaluator.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((evaluation) => evaluation.status === statusFilter);
    }

    if (branchFilter !== 'all') {
      filtered = filtered.filter((evaluation) => evaluation.branch === branchFilter);
    }

    if (centerFilter !== 'all') {
      filtered = filtered.filter((evaluation) => evaluation.center === centerFilter);
    }

    setFilteredEvaluations(filtered);
    if (!didMountRef.current) {
      didMountRef.current = true;
    } else {
      setCurrentPage(1);
    }
  }, [searchTerm, statusFilter, branchFilter, centerFilter, evaluations, setCurrentPage]);

  // Handle view candidate - use admin path in admin view
  const handleViewCandidate = (candidateId) => {
    if (isAdminView) {
      navigate(`/admin/candidates/${candidateId}`);
    } else {
      navigate(`/committee/candidates/${candidateId}`);
    }
  };

  // Handle edit evaluation - committees only (admin cannot access committee route)
  const handleEditEvaluation = (candidateId) => {
    if (!isAdminView) {
      navigate(`/committee/evaluate/${candidateId}`);
    }
  };

  // Get branch name - always use branch_name directly from the data if available
  const getBranchName = (branchId) => {
    if (typeof branchId === 'object' && branchId !== null) {
      return branchId.name || '';
    }
    const branch = config.organization.branches.find(b => b.id === branchId);
    return branch ? branch.name : (branchId || '');
  };

  // Get center name - always use center_name directly from the data if available
  const getCenterName = (centerId) => {
    if (typeof centerId === 'object' && centerId !== null) {
      return centerId.name || '';
    }
    const center = config.organization.centers.find(c => c.id === centerId);
    return center ? center.name : (centerId || '');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA');
  };

  // Get status color scheme based on evaluation status
  const getStatusColorScheme = (evaluation) => {
    if (evaluation.status) {
      switch (evaluation.status.toLowerCase()) {
        case 'draft':
          return 'gray';
        case 'submitted':
          return 'green';
        case 'pending':
          return 'purple';
        default:
          return 'gray';
      }
    }
    return 'gray';
  };

  // Get status label based on evaluation status
  const getStatusLabel = (evaluation) => {
    if (evaluation.status) {
      switch (evaluation.status.toLowerCase()) {
        case 'draft':
          return 'مسودة';
        case 'submitted':
          return 'تم التقييم';
        case 'pending':
          return 'في انتظار النتيجة';
        default:
          return 'غير معروف';
      }
    }
    return 'غير معروف';
  };

  // Get recommendation text in Arabic
  const getRecommendationText = (recommendation) => {
    switch (recommendation) {
      case 'highly_recommended':
        return 'موصى به بشدة';
      case 'recommended':
        return 'موصى به';
      case 'recommended_with_reservations':
        return 'موصى به مع تحفظات';
      case 'not_recommended':
        return 'غير موصى به';
      default:
        return 'لم يتم التحديد';
    }
  };

  // Get recommendation badge color
  const getRecommendationBadgeColor = (recommendation) => {
    switch (recommendation) {
      case 'highly_recommended':
        return 'green';
      case 'recommended':
        return 'teal';
      case 'recommended_with_reservations':
        return 'orange';
      case 'not_recommended':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Get score color based on value
  const getScoreColor = (score) => {
    if (score >= 8) return 'green.500';
    if (score >= 6) return 'yellow.500';
    if (score >= 4) return 'orange.500';
    return 'red.500';
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8}>
        <Heading as="h1" size="lg" mb={2}>
          التقييمات
        </Heading>
        <Text color="gray.600">
          قائمة التقييمات المقدمة من قبل أعضاء اللجنة
        </Text>
      </Box>

      {/* Filters */}
      <Box mb={6} p={4} bg="white" borderRadius="lg" boxShadow="sm">
        <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
          {/* Search */}
          <InputGroup maxW={{ base: '100%', md: '300px' }}>
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="بحث باسم المرشح أو المقيم"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          {/* Status Filter */}
          <Select
            placeholder="تصفية حسب الحالة"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
          >
            <option value="all">جميع الحالات</option>
            <option value="submitted">تم التقييم</option>
            <option value="draft">مسودة</option>
          </Select>

          {/* Branch Filter - Disabled for committee members as they can only see their own branch */}
          <Select
            placeholder="الفرع"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            isDisabled={isCommittee}
            maxW={{ base: '100%', md: '200px' }}
          >
            <option value="all">جميع الفروع</option>
            {config.organization.branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>

          {/* Center Filter */}
          <Select
            placeholder="تصفية حسب المركز"
            value={centerFilter}
            onChange={(e) => setCenterFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
          >
            <option value="all">جميع المراكز</option>
            {config.organization.centers
              .filter((center) => {
                if (branchFilter === 'all') return true;
                return center.branchId === branchFilter;
              })
              .map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
          </Select>

          {/* Clear Filters Button */}
          <Button
            leftIcon={<FiX />}
            colorScheme={isAnyFilterActive ? 'red' : 'gray'}
            variant={isAnyFilterActive ? 'solid' : 'outline'}
            onClick={clearAllFilters}
            ml={{ base: 0, md: 'auto' }}
            title={isAnyFilterActive ? 'هناك عوامل تصفية مفعّلة - انقر لمسحها' : 'لا توجد عوامل تصفية مفعّلة'}
          >
            مسح التصفية
          </Button>
        </Stack>
      </Box>

      {/* Evaluations Table */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>المرشح</Th>
              <Th>الفرع</Th>
              <Th>المركز</Th>
              <Th>حالة الترشيح</Th>
              <Th>التقييم</Th>
              <Th>التوصية</Th>
              <Th>تاريخ التقييم</Th>
              <Th>الحالة</Th>
              <Th>الإجراءات</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={9} textAlign="center" py={10}>
                  جاري تحميل البيانات...
                </Td>
              </Tr>
            ) : currentEvaluations.length === 0 ? (
              <Tr>
                <Td colSpan={9} textAlign="center" py={10}>
                  لا توجد تقييمات متاحة
                </Td>
              </Tr>
            ) : (
              currentEvaluations.map((evaluation) => (
                <Tr key={evaluation.id}>
                  <Td>{evaluation.candidateName}</Td>
                  <Td>{evaluation.branchName}</Td>
                  <Td>{evaluation.centerName}</Td>
                  <Td>
                    <Badge
                      colorScheme={getNominationStatusColor(evaluation?.nominationStatus)}
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      {getNominationStatusLabel(evaluation?.nominationStatus)}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontWeight="bold" color={getScoreColor(evaluation.score)}>
                      {evaluation.score.toFixed(1)}/10
                      {evaluation.totalQuestions > 0 }
                    </Text>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={getRecommendationBadgeColor(evaluation.recommendation)}
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      {getRecommendationText(evaluation.recommendation)}
                    </Badge>
                  </Td>
                  <Td>{formatDate(evaluation.submissionDate)}</Td>
                  <Td>
                    <Badge
                      colorScheme={getStatusColorScheme(evaluation)}
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      {getStatusLabel(evaluation)}
                    </Badge>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="sm"
                        aria-label="المزيد من الخيارات"
                      />
                      <MenuList>
                        <MenuItem icon={<FiEye />} onClick={() => handleViewCandidate(evaluation.candidateId)}>
                          عرض المرشح
                        </MenuItem>
                        {!isAdminView && evaluation.status === 'draft' && (
                          <MenuItem icon={<FiEdit2 />} onClick={() => handleEditEvaluation(evaluation.candidateId)}>
                            تعديل التقييم
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>

        {/* Pagination and page size */}
        {!loading && filteredEvaluations.length > 0 && (
          <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor="gray.200">
            <Text color="gray.600">
              عرض {startIndex + 1} إلى {Math.min(endIndex, filteredEvaluations.length)} من أصل {filteredEvaluations.length} تقييم
            </Text>
            <Stack direction="row" spacing={3} align="center">
              <Select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                width="90px"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
              <IconButton
                icon={<FiChevronRight />}
                aria-label="الصفحة السابقة"
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              <IconButton
                icon={<FiChevronLeft />}
                aria-label="الصفحة التالية"
                isDisabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </Stack>
          </Flex>
        )}
      </Box>
    </Container>
  );
};

export default CommitteeEvaluations;
